import { useRef, useState } from 'react';
import './SearchBar.css';
import { findProjects, getProjects } from '../../utilities/api/projectsTimeline';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { findForms, getIntakeForms } from '../../utilities/api/allForms';
import { findPatentApplications, getPatentApplications } from '../../utilities/api/patentApplications';
import { findTrademarkApplications, getTrademarkApplications } from '../../utilities/api/trademarkApplications';

export default function SearchBar({ user, section, updateContent, updatePatents, updateTrademarks }) {
  const [textInput, setTextInput] = useState('');
  const [searching, setSearching] = useState(false);
  const debounceTimeoutRef = useRef(null);
  const currentQueryRef = useRef(null);

  const handleChange = async (evt) => {
    const searchQuery = evt.target.value;

    // Clear previous debounce timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    // Create a unique query ID to track this search
    const queryId = Date.now();
    currentQueryRef.current = queryId;
    
    if (searchQuery) {
      setSearching(true);
      
      // Debounce the search by 300ms
      debounceTimeoutRef.current = setTimeout(async () => {
        try {
          // Only proceed if this is still the current query
          if (currentQueryRef.current !== queryId) return;
          
          const results = await handleSearch(section, searchQuery);
          
          // Check again after async operation completes
          if (currentQueryRef.current !== queryId) return;
          
          if (section === 'applications') {
            await updatePatents(results.patents);
            await updateTrademarks(results.trademarks);
          } else {
            await updateContent(results);
          }
        } catch (error) {
          console.error('Error fetching search results:', error);
        } finally {
          // Only update state if this is still the current query
          if (currentQueryRef.current === queryId) {
            setTextInput(searchQuery);
            setSearching(false);
          }
        }
      }, 300);
    } else {
      await handleClear();
    }
  }

  const handleSearch = async (section, searchQuery) => {
    try {
      if (section === 'projects') {
        const response = await findProjects(user, searchQuery);
        return response;
      } else if (section === 'forms') {
        const response = await findForms(user, searchQuery);
        return response;
      } else if (section === 'applications') {
        const patentResults = await findPatentApplications(user, searchQuery);
        const trademarkResults = await findTrademarkApplications(user, searchQuery);
        return { patents: patentResults, trademarks: trademarkResults };
      }
      return [];
    } catch (error) {
      console.error('Error fetching search results:', error);
    }
  }

  const handleClear = async () => {
    setTextInput('');
    document.getElementById('Search').value = '';
    try {
      setSearching(true);
      // Restore original data
      if (section === 'projects') {
        const restoredData = await getProjects(user);
        await updateContent(restoredData);
      } else if (section === 'forms') {
        const restoredData = await getIntakeForms();
        await updateContent(restoredData);
      } else if (section === 'applications') {
        const [restoredPatents, restoredTrademarks] = await Promise.all([
          getPatentApplications(user),
          getTrademarkApplications(user)
        ]);
        await updatePatents(restoredPatents);
        await updateTrademarks(restoredTrademarks);
      } else {
        await updateContent([]);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setSearching(false);
    }
  }

  return (
    <div className='SearchBar'>
      <div className='searchContainer'>
        <svg width="16" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7.13677 1C5.92303 1 4.73655 1.35992 3.72737 2.03423C2.71818 2.70855 1.93161 3.66698 1.46714 4.78833C1.00266 5.90968 0.881131 7.14358 1.11792 8.33399C1.35471 9.52441 1.93918 10.6179 2.79742 11.4761C3.65566 12.3344 4.74913 12.9188 5.93955 13.1556C7.12996 13.3924 8.36386 13.2709 9.48521 12.8064C10.6066 12.3419 11.565 11.5554 12.2393 10.5462C12.9136 9.53699 13.2735 8.35051 13.2735 7.13677C13.2734 5.50923 12.6269 3.94838 11.476 2.79753C10.3252 1.64669 8.76431 1.0001 7.13677 1Z" stroke="#1B2B41" strokeOpacity="0.8" strokeWidth="2" strokeMiterlimit="10"/>
          <path d="M12.4851 11.7141L16.771 16" stroke="#1B2B41" strokeOpacity="0.8" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round"/>
        </svg>
        <input className='searchInput' type="text" name="Search" id="Search" placeholder='Search' onChange={handleChange} />
        {searching &&
        <LoadingSpinner message='' size='small' />
        }
        {textInput &&
        <svg className='clearButton' onClick={handleClear} width="18" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="16.2666" cy="16.2666" r="15.7666" stroke="#EEEEEE"/>
          <path d="M21.1093 12L12 21.1093M12 12L21.1093 21.1093" stroke="#1B2B41" strokeOpacity="0.8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        }
      </div>
    </div>
  )
}
